import { useEffect, type RefObject } from 'react';
import { ehToque, prefereMovimentoReduzido } from './use-em-vista';

function limitar(v: number, max: number): number {
  return Math.max(-max, Math.min(max, v));
}

/**
 * Inclina um elemento conforme o APARELHO se move — escrevendo
 * `--incl-x`/`--incl-y` (em graus) direto no elemento, nunca por estado:
 * `deviceorientation` chega a ~60 vezes por segundo, e um `setState` por
 * evento seria um render da árvore por quadro (a mesma razão do
 * `useHolofote`). Quem consome monta o transform com
 * `calc(<base> + var(--incl-x, 0deg))`, e a `transition-transform` que o
 * elemento já tem suaviza cada mudança.
 *
 * ## Dois caminhos, escolhidos pelo que o aparelho PERMITE
 * - **Android**: giroscópio direto (`deviceorientation` dispara sem
 *   permissão). A primeira leitura vira o ZERO — a pessoa segura o celular
 *   como quiser, e o cartão responde ao desvio, não à postura.
 * - **iPhone**: o Safari exige `requestPermission()` a partir de um toque —
 *   um prompt de sensor no meio de uma landing espanta em vez de encantar.
 *   Fallback: a ROLAGEM inclina, pela velocidade com decaimento — o cartão
 *   responde ao gesto do dedo e volta ao repouso sozinho.
 *
 * Desktop fica de fora (o parallax de mouse já existe); reduced-motion
 * também. Sem nada disso, as variáveis simplesmente não são escritas e o
 * `var(..., 0deg)` mantém o transform de sempre — inclusive no pré-render.
 */
export function useInclinacao<T extends HTMLElement>(
  alvo: RefObject<T | null>,
  maxGraus = 4,
): void {
  useEffect(() => {
    const elemento = alvo.current;
    if (!elemento) return undefined;
    if (!ehToque() || prefereMovimentoReduzido()) return undefined;

    const escrever = (rx: number, ry: number): void => {
      elemento.style.setProperty('--incl-x', `${rx.toFixed(2)}deg`);
      elemento.style.setProperty('--incl-y', `${ry.toFixed(2)}deg`);
    };

    const doe = window.DeviceOrientationEvent as
      | (typeof DeviceOrientationEvent & { requestPermission?: () => Promise<string> })
      | undefined;

    // iPhone (o único que expõe requestPermission): rolagem em vez de sensor.
    if (doe && typeof doe.requestPermission === 'function') {
      let ultimoY = window.scrollY;
      let alvoRy = 0;
      let atualRy = 0;
      let quadro = 0;
      let vivo = true;

      const passo = (): void => {
        if (!vivo) return;
        // Decaimento exponencial: o cartão persegue a velocidade da rolagem
        // e assenta quando ela cessa — nada de estado preso inclinado.
        alvoRy *= 0.92;
        atualRy += (alvoRy - atualRy) * 0.15;
        escrever(limitar(atualRy * 0.5, maxGraus / 2), limitar(atualRy, maxGraus));
        if (Math.abs(atualRy) > 0.01 || Math.abs(alvoRy) > 0.01) {
          quadro = requestAnimationFrame(passo);
        }
      };

      const aoRolar = (): void => {
        const delta = window.scrollY - ultimoY;
        ultimoY = window.scrollY;
        alvoRy = limitar(alvoRy + delta * 0.06, maxGraus);
        cancelAnimationFrame(quadro);
        quadro = requestAnimationFrame(passo);
      };

      window.addEventListener('scroll', aoRolar, { passive: true });
      return () => {
        vivo = false;
        window.removeEventListener('scroll', aoRolar);
        cancelAnimationFrame(quadro);
      };
    }

    // Android: o sensor, com a primeira leitura como zero.
    let base: { beta: number; gamma: number } | null = null;
    let quadro = 0;
    const aoOrientar = (evento: DeviceOrientationEvent): void => {
      const beta = evento.beta ?? 0;
      const gamma = evento.gamma ?? 0;
      base ??= { beta, gamma };
      const b = base;
      cancelAnimationFrame(quadro);
      quadro = requestAnimationFrame(() => {
        escrever(
          limitar((beta - b.beta) / 6, maxGraus),
          limitar((gamma - b.gamma) / 6, maxGraus),
        );
      });
    };

    window.addEventListener('deviceorientation', aoOrientar);
    return () => {
      window.removeEventListener('deviceorientation', aoOrientar);
      cancelAnimationFrame(quadro);
    };
  }, [alvo, maxGraus]);
}
